import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../api';
import { Spinner, Alert, StatusBadge, EmptyState, SortHeader, nextSort, sortRows } from '../../components/UI';
import { useConfirm } from '../../components/ConfirmProvider';

function formatDate(ts) {
  if (!ts) return '-';
  const d = new Date(ts);
  return isNaN(d) ? '-' : d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}
/** Multi-select checkbox dropdown for filtering the attempts table by paper. */
function PaperFilter({ papers, selected, onToggle, onClear }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const onKey = e => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDoc); document.removeEventListener('keydown', onKey); };
  }, [open]);

  const count = selected.size;
  const label = count === 0 ? 'All papers'
    : count === 1 ? (papers.find(p => selected.has(p.id))?.title || '1 paper')
    : `${count} papers`;

  return (
    <div className="filter-dropdown" ref={ref}>
      <button type="button" className={`filter-toggle${count ? ' has-value' : ''}`}
        aria-expanded={open} onClick={() => setOpen(o => !o)}>
        <span className="filter-toggle-label">Paper: <strong>{label}</strong></span>
        <svg className="chev" width="14" height="14" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </button>
      {open && (
        <div className="filter-menu">
          <div className="filter-menu-head">
            <span>Filter by paper</span>
            {count > 0 && <button type="button" className="filter-clear" onClick={onClear}>Clear</button>}
          </div>
          <div className="filter-menu-list">
            {papers.map(p => (
              <label key={p.id} className="filter-option">
                <input type="checkbox" checked={selected.has(p.id)} onChange={() => onToggle(p.id)} />
                <span className="filter-option-text">{p.title}</span>
                <span className="filter-option-count">{p.count}</span>
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// How each sortable column pulls its comparable value out of an attempt row.
const SORT_ACCESSORS = {
  candidate: a => (a.user_name || '').toLowerCase(),
  paper: a => (a.paper_title || '').toLowerCase(),
  date: a => (a.submitted_at ? new Date(a.submitted_at).getTime() : 0),
  status: a => (a.status || '').toLowerCase(),
  score: a => (Number(a.max_score) > 0 ? Number(a.total_score) / Number(a.max_score) : -1),
  tabs: a => Number(a.tab_switches) || 0,
  published: a => (a.is_published ? 1 : 0),
};

export default function Results() {
  const confirm = useConfirm();
  const [tab, setTab] = useState('attempts');
  const [attempts, setAttempts] = useState(null);
  const [queue, setQueue] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [scores, setScores] = useState({}); // answer_id -> score input value
  const [busy, setBusy] = useState(false);
  const [selectedPapers, setSelectedPapers] = useState(() => new Set()); // paper_ids; empty = all
  const [sort, setSort] = useState({ key: null, dir: 'asc' }); // null = server order (newest first)

  const onSort = col => setSort(s => nextSort(s, col));

  // Distinct papers present in the loaded attempts, with a count each, for the filter.
  const paperOptions = [...(attempts || []).reduce((m, a) => {
    const e = m.get(a.paper_id) || { id: a.paper_id, title: a.paper_title, count: 0 };
    e.count++; m.set(a.paper_id, e);
    return m;
  }, new Map()).values()].sort((x, y) => x.title.localeCompare(y.title));

  const togglePaper = id => setSelectedPapers(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const visibleAttempts = sortRows(
    (attempts || []).filter(a => selectedPapers.size === 0 || selectedPapers.has(a.paper_id)),
    sort, SORT_ACCESSORS);

  const loadAttempts = useCallback(() => api.get('/results').then(setAttempts).catch(e => setError(e.message)), []);
  const loadQueue = useCallback(() => api.get('/results/review-queue').then(setQueue).catch(e => setError(e.message)), []);

  useEffect(() => { loadAttempts(); loadQueue(); }, [loadAttempts, loadQueue]);

  async function submitScore(answerId) {
    setError(''); setSuccess('');
    try {
      await api.put(`/results/answers/${answerId}`, { awarded_score: Number(scores[answerId]) });
      setSuccess('Score saved.');
      await loadQueue();
      await loadAttempts();
    } catch (e) { setError(e.message); }
  }

  async function publishAll() {
    if (!(await confirm({ title: 'Publish results', message: 'Publish all fully-evaluated attempts (no pending AI reviews)?', confirmText: 'Publish' }))) return;
    setBusy(true); setError(''); setSuccess('');
    const evaluated = (attempts || []).filter(a => a.status === 'evaluated' && !a.is_published);
    if (!evaluated.length) { setBusy(false); setSuccess('Nothing to publish.'); return; }
    try {
      const res = await api.post('/results/publish', { attempt_ids: evaluated.map(a => a.id) });
      setSuccess(`Published ${res.published} result(s).`);
      await loadAttempts();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  async function publishOne(a) {
    setError(''); setSuccess('');
    try {
      await api.post('/results/publish', { attempt_ids: [a.id] });
      await loadAttempts();
    } catch (e) { setError(e.message); }
  }

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="eyebrow">Results</div>
          <h1>Results &amp; review</h1>
        </div>
        <button className="btn btn-primary" onClick={publishAll} disabled={busy}>Publish all evaluated</button>
      </div>

      <Alert type="danger">{error}</Alert>
      <Alert type="success">{success}</Alert>

      <div className="tabs">
        <div className={`tab ${tab === 'attempts' ? 'active' : ''}`} onClick={() => setTab('attempts')}>All attempts</div>
        <div className={`tab ${tab === 'queue' ? 'active' : ''}`} onClick={() => setTab('queue')}>
          AI review queue {queue && queue.length > 0 && <span className="badge badge-warning" style={{ marginLeft: 6 }}>{queue.length}</span>}
        </div>
        {tab === 'attempts' && attempts && attempts.length > 0 && (
          <div className="tabs-aside">
            {/* {selectedPapers.size > 0 && (
              <span className="text-sm text-muted">Showing {visibleAttempts.length} of {attempts.length}</span>
            )} */}
            <PaperFilter papers={paperOptions} selected={selectedPapers}
              onToggle={togglePaper} onClear={() => setSelectedPapers(new Set())} />
          </div>
        )}
      </div>

      {tab === 'attempts' && (
        !attempts && !error ? <Spinner /> : attempts && attempts.length === 0 ? (
          <div className="card"><EmptyState title="No attempts yet" /></div>
        ) : (
          <>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr>
                    <SortHeader label="Candidate" col="candidate" sort={sort} onSort={onSort} />
                    <SortHeader label="Paper" col="paper" sort={sort} onSort={onSort} />
                    <SortHeader label="Attempt date" col="date" sort={sort} onSort={onSort} />
                    <SortHeader label="Status" col="status" sort={sort} onSort={onSort} />
                    <SortHeader label="Score" col="score" sort={sort} onSort={onSort} />
                    <SortHeader label="Tab switches" col="tabs" sort={sort} onSort={onSort} />
                    <SortHeader label="Published" col="published" sort={sort} onSort={onSort} />
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {visibleAttempts.map(a => (
                    <tr key={a.id}>
                      <td>{a.user_name}<div className="text-sm text-muted mono">{a.mobile}</div></td>
                      <td>{a.paper_title}</td>
                      <td className="text-nowrap">{formatDate(a.submitted_at)}</td>
                      <td>
                        <StatusBadge status={a.status} />
                        {a.pending_reviews > 0 && <div className="text-sm text-muted mt-2">{a.pending_reviews} pending AI review</div>}
                      </td>
                      <td className="mono">{a.total_score ?? '-'} / {a.max_score ?? '-'}</td>
                      <td className="mono">{a.tab_switches}</td>
                      <td>{a.is_published ? <StatusBadge status="reviewed" /> : <span className="text-muted text-sm">No</span>}</td>
                      <td className="table-actions">
                        <Link to={`/admin/results/${a.id}`} className="btn btn-outline btn-sm">View</Link>
                        {a.status === 'evaluated' && !a.is_published && (
                          <button className="btn btn-primary btn-sm" onClick={() => publishOne(a)}>Publish</button>
                        )}
                      </td>
                    </tr>
                  ))}
                  {visibleAttempts.length === 0 && (
                    <tr><td colSpan={8} className="text-sm text-muted">No attempts match the selected paper(s).</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </>
        )
      )}

      {tab === 'queue' && (
        !queue && !error ? <Spinner /> : queue && queue.length === 0 ? (
          <div className="card"><EmptyState title="Queue is empty" sub="No AI-scored answers are waiting for review." /></div>
        ) : (
          <div>
            {(queue || []).map(item => (
              <div className="card mt-4" key={item.answer_id}>
                <div className="flex-between">
                  <div className="text-sm text-muted">{item.user_name} &middot; {item.paper_title}</div>
                  <span className="badge badge-accent">AI suggested {item.awarded_score} / {item.max_score}</span>
                </div>
                <h3 style={{ marginTop: 8 }}>{item.question_text}</h3>
                <p className="text-sm"><strong>Candidate answer:</strong> {item.answer_text || '(no answer)'}</p>
                {item.correct_answer && <p className="text-sm text-muted"><strong>Reference:</strong> {item.correct_answer}</p>}
                {item.ai_justification && <p className="text-sm text-muted"><strong>AI note:</strong> {item.ai_justification}</p>}
                <div className="flex-gap mt-4">
                  <input
                    type="number" min={0} max={item.max_score} step={0.5}
                    style={{ width: 100 }}
                    placeholder={String(item.awarded_score)}
                    value={scores[item.answer_id] ?? item.awarded_score}
                    onChange={e => setScores(s => ({ ...s, [item.answer_id]: e.target.value }))}
                  />
                  <button className="btn btn-primary btn-sm" onClick={() => submitScore(item.answer_id)}>Approve score</button>
                </div>
              </div>
            ))}
          </div>
        )
      )}
    </div>
  );
}
